import { SECOND_MS } from '@/constants';

export function generateRandomHash(
  length: number,
  characters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
): string {
  const charactersLength: number = characters.length;
  let hash: string = '';

  for (let i = 0; i < length; i++) {
    hash += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return hash;
}

export async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function retry<T = unknown>(
  fn: () => Promise<T>,
  count: number = 5,
  retryDuration: number = SECOND_MS,
): Promise<T> {
  let retryCount = 0;
  let error: Error | undefined;

  while (retryCount < count) {
    try {
      const result: T = await fn();
      return result;
    } catch (e) {
      if (++retryCount >= count) {
        error = e as Error;
        break;
      } else {
        await sleep(retryDuration);
      }
    }
  }

  throw error;
}

export function checkIsBackspace(event: KeyboardEvent): boolean {
  return (
    event.keyCode === 10009 ||
    event.keyCode === 461 ||
    event.code === 'Backspace' ||
    event.code === 'Escape' ||
    event.key === 'Backspace' ||
    event.key === 'Escape'
  );
}

interface WithTimeoutProps<T> {
  promise: Promise<T>;
  timeoutMs: number;
  fallback: T;
}

export async function withTimeout<T>({ promise, timeoutMs, fallback }: WithTimeoutProps<T>): Promise<T> {
  return Promise.race([promise, sleep(timeoutMs).then(() => fallback)]);
}

const AUTO_PLAY_TIMEOUT_MS = 2 * SECOND_MS;

export async function checkCanAutoPlay(): Promise<boolean> {
  const play = async (): Promise<boolean> => {
    const audio = document.createElement('audio');
    audio.src = require('@/assets/sounds/button_press.mp3');

    await audio.play();

    audio.pause();

    return true;
  };

  const canAutoPlay = await withTimeout({
    promise: play().catch(() => false),
    timeoutMs: AUTO_PLAY_TIMEOUT_MS,
    fallback: false,
  });

  sessionStorage.setItem('autoPlay', String(canAutoPlay));

  return canAutoPlay;
}

export function getResolution(value: Blob | string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = typeof value === 'string' ? value : URL.createObjectURL(value);

    img.onload = () => {
      const width = img.width;
      const height = img.height;
      URL.revokeObjectURL(url);
      resolve({ width, height });
    };
    img.onerror = (e) => {
      reject(e);
    };

    img.src = url;
  });
}
